"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetcher } from "@/lib/http/fetcher";
import type { CartItemForClient, ProductForClient, Variant } from "@/lib/shared/types/interfaces";
import { getTotal } from "@/lib/shared/utils/calculatePrice";
import { useCountryStore } from "./useCountryStore";

const CART_STORAGE_KEY = "tutuly-shopping-cart";

interface ShoppingCartStore {
  isOpen: boolean;
  userId: string | null;
  setIsOpen: (isOpen: boolean) => void;
  setUserId: (userId: string | null) => void;
  cartItems: CartItemForClient[];
  setCartItems: (items: CartItemForClient[]) => void;
  subTotal: () => number;
  addItem: (product: ProductForClient, variant: Variant, quantity?: number) => void;
  removeItem: (item: CartItemForClient) => void;
}

function toPersistedCartItems(items: CartItemForClient[]) {
  return items.map((item) => ({
    product: item.product.id,
    variant: item.variant.id,
    quantity: item.quantity,
  }));
}

async function persistCartItemsToServer(userId: string | null, items: CartItemForClient[]) {
  if (!userId) return;
  try {
    await fetcher<{ ok: boolean }>("/api/users/cart", {
      method: "PATCH",
      body: JSON.stringify({
        cartItems: toPersistedCartItems(items),
      }),
    });
  } catch (error) {
    console.error("Failed to persist cart items:", error);
  }
}

/**
 * Shopping cart store
 * Manages cart items, totals, and cart dialog state.
 * Guest carts survive full page loads via localStorage (account carts still sync to the API).
 */
const useShoppingCartStore = create<ShoppingCartStore>()(
  persist(
    (set, get) => ({
      isOpen: false,
      userId: null,
      setIsOpen: (isOpen) => set({ isOpen }),
      setUserId: (userId) => set({ userId }),
      cartItems: [],
      setCartItems: (items: CartItemForClient[]) =>
        set((state) => {
          void persistCartItemsToServer(state.userId, items);
          return { cartItems: items };
        }),
      subTotal: () => getTotal(get().cartItems, useCountryStore.getState().country.exchangeRate),
      addItem: (product: ProductForClient, variant: Variant, quantity?: number) =>
        set((state) => {
          const isNotNew = state.cartItems.some(
            (item) => item.product.id === product.id && item.variant.id === variant.id,
          );

          let cartItems: CartItemForClient[] = [];
          if (isNotNew) {
            const qty = quantity ?? 1;
            cartItems = state.cartItems.map((item) => {
              if (item.product.id === product.id && item.variant.id === variant.id) {
                return { ...item, quantity: item.quantity + qty };
              }
              return item;
            });
          } else {
            cartItems = [{ product, variant, quantity: quantity ?? 1 }, ...state.cartItems];
          }
          void persistCartItemsToServer(state.userId, cartItems);
          return { cartItems };
        }),

      removeItem: (cartItem: CartItemForClient) =>
        set((state) => {
          const cartItems = [
            ...state.cartItems.filter(
              (item) =>
                item.product.id + item.variant.variant !==
                cartItem.product.id + cartItem.variant.variant,
            ),
          ];
          void persistCartItemsToServer(state.userId, cartItems);
          return { cartItems };
        }),
    }),
    {
      name: CART_STORAGE_KEY,
      partialize: (state) => ({ cartItems: state.cartItems }),
    },
  ),
);

export default useShoppingCartStore;
